/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEvent, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles, Typography} from '@material-ui/core';
import {ClassNameMap} from '@material-ui/styles/withStyles';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import Device from '@ion/core/src/services/Device';
import {AssetType, Crumb, ToolAssetType} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import IonSpinner from '@ion/dls/src/components/loading/IonSpinner';
import LoadingText from '@ion/dls/src/components/loading/LoadingText';
import {AssetTypeIcon} from '@ion/dls/src/icons/AssetTypeIcon';
import {NotificationUrgentIcon} from '@ion/dls/src/icons/NotificationUrgentIcon';
import {ScDeviceIcon} from '@ion/dls/src/icons/ScDeviceIcon';
import {ErrorState} from '@ion/dls/src/notifications/ErrorState';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import {
  getDisconnectedToolReport,
  getSelectedAssetTools,
  setAssetDetailView,
  setFindButtonClickStatus,
  setSelectedAsset
} from '@/store/actions/tracking';
import {isFindNearbySelector, isToolsLoadingSelector} from '@/store/selectors/tracking-selectors';
import {openToolDetailPanel} from './toolDetailActions';
import {getAssetIconType} from './trackingUtils';

export type ContainedAssetProps = {
  asset: Device;
  crumbs?: Crumb[],
  setCrumbs?: (param: Crumb[]) => void;
  handleCrumbClick?: (val) => void;
  panelLoading?: number | null;
}

type NoToolsProps = {
  classes: ClassNameMap;
  error?: ServiceError;
  retry?: Function
}

const useStyles = makeStyles((theme: DLSTheme) => {
  const {
    dls: {
      colors: {
        aaBlue,
        grey3
      },
      motion: {
        element: {
          largeExpansion: {
            close: closeLargeExpansion,
            open: openLargeExpansion
          }
        }
      }
    },
    typography: {
      fontFamily
    }
  } = theme;
  return {
    container : {
      borderTop : `1px dashed ${dlsColor('grey4')}`,
      marginTop : 10
    },
    containedAssetWrapper : {
      display    : 'none',
      maxHeight  : '0',
      transition : `max-height ${closeLargeExpansion}`
    },
    containedAssetWrapperOpen : {
      maxHeight  : '800px',
      transition : `max-height ${openLargeExpansion}`
    },
    toolDisplay : {
      display       : 'flex',
      flexDirection : 'column',
      padding       : '10px 0 0'
    },
    toolItem : {
      color      : dlsColor('grey3'),
      width      : '100%',
      display    : 'flex',
      alignItems : 'center',
      padding    : '3px 0',
      '&:hover'  : {
        cursor : 'pointer',
        '& p'  : {
          textDecoration : 'underline'
        }
      }
    },
    indicator : {
      textAlign      : 'center',
      display        : 'flex',
      justifyContent : 'center',
      alignItems     : 'center',
      fontSize       : '14px',
      fontFamily     : 'Roboto',
      lineHeight     : '24px',
      height         : '152px',
      fontWeight     : 400,
      color          : dlsColor('grey2')

    },
    toolText : {
      color        : grey3,
      fontFamily   : fontFamily,
      fontSize     : '14px',
      lineHeight   : '16px',
      maxWidth     : '290px',
      textOverflow : 'ellipsis',
      whiteSpace   : 'nowrap',
      overflow     : 'hidden'
    },
    iconContainer : {
      padding : '3px 2px 0'
    },
    onlineText : {
      '&&&&' : {
        color : aaBlue
      }
    },
    offlineText : {
      '&&' : {
        color : grey3
      }
    },
    loading : {
      display        : 'flex',
      justifyContent : 'center',
      alignItems     : 'center'
    },
    spinnerWrapper : {
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'center',
      height         : '152px'
    }
  };
});

// defines the component which provides the tools status indication, either loading
// or no tools available ...
const StatusText = ({classes, text}: {text: string, classes: ClassNameMap}) => (
  <p className={classes.indicator}>{text}</p>
);

// defines the component which is rendered when an asset either does not contain any
// tools, or when an error occurs while retrieving the tools ...
const NoTools = ({classes, error, retry}: NoToolsProps) => {
  const isError = error && !error?.isNotFoundError;
  if (isError) {
    return (
      <div style={{paddingTop: 20}}>
        <ErrorState show onRefresh={function(e) {
          e.stopPropagation();
          retry();
        }} />
      </div>
    );
  }
  return <StatusText classes={classes} text={translate('tracking:indicators.noResults')} />;
};

/**
 * ContainedAsset is what goes in the accordion dropdown for the contains
 * property in an asset card when it is opened
 *
 * ```typescript
 * import React from 'react';
 *
 * <ContainedAsset asset={asset}
 *  crumbs={crumbs}
 *  setCrumbs={onSetCrumbs}
 *  handleBack={handleBackFunction} />
 *
 * ```
 */
const ContainedAsset = (props: ContainedAssetProps) => {
  const classes                   = useStyles();
  const [
    findNearBy,
    findNearByState
  ]                               = useSelector(isFindNearbySelector);
  const isToolsLoading            = useSelector(isToolsLoadingSelector);
  const allAssets                 = useSelector(({tracking}) =>
    tracking.fleetMode.isLargeFleetMode ? tracking.fleetMode.largeFleetAssetList.assets : tracking.assets.assets);
  const selectedAsset             = useSelector(({tracking}) => tracking.assets.selectedAsset);
  const selectedAssetTools        = useSelector(({tracking}) => tracking.assets.selectedAssetTools);
  const toolsError                = useSelector(({tracking}) => tracking.tools.toolsError);
  const toolPanelOpen             = useSelector(({tracking}) => tracking.tools.toolPanelOpen);
  const disconnectedToolTagReport = useSelector(
    ({tracking}) => tracking.tools.disconnectedToolTagReport
  );
  const isToolDisplayed                  = useRef<boolean>(false);
  const dispatch                         = useDispatch();
  const [fetchingTool, setFetchingTool]  = useState({
    tool : null,
    busy : false
  });
  const {
    asset,
    panelLoading
  } = props;
  const isMatchingAsset = toolPanelOpen.find(item => asset?.id() === item?.assetId);
  const assetTools      = selectedAssetTools.find(toolsAsset => asset?.id() === toolsAsset.assetId)?.tools;

  async function selectTool(e: MouseEvent, tool: ToolAssetType) {
    e.stopPropagation();
    let parentAsset = null;
    // Prevent additional API call if user is just choosing a disconnected tool
    // that we already have data for
    const isSameDisconnectedTool = tool.name === disconnectedToolTagReport?.assetName;

    if (!tool.tagReport && !isSameDisconnectedTool) {
      setFetchingTool({
        tool : tool.id,
        busy : true
      });
      dispatch(getDisconnectedToolReport(tool));
      setFetchingTool(
        {
          tool : null,
          busy : false
        }
      );
    };

    parentAsset = asset;
    dispatch(setSelectedAsset({asset: parentAsset}) as any);
    isToolDisplayed.current = true;
    setTimeout(() => {
      isToolDisplayed.current && showToolDetail(tool, parentAsset);
    }, 350);
  };

  function showToolDetail(tool, parentAsset = null) {
    if (tool && parentAsset) {
      isToolDisplayed.current = false;
      openToolDetailPanel(dispatch, tool);
      if (findNearBy.miles) {
        const parentAssetForAssets = allAssets.find(asset => entityId(tool.assets[0]) === asset?.getAssetValue?.('id'));
        if (findNearBy.miles && findNearByState.showPopup) {
          dispatch(setFindButtonClickStatus(false));
        }
        const asset = parentAsset ?? parentAssetForAssets;
        dispatch(setSelectedAsset({asset}) as any);
      }
    }
  };

  function retry() {
    dispatch(getSelectedAssetTools(asset?.getDevice?.(), true));
  }

  const ToolListDisplay = () => (
    assetTools?.length ? (
      <div className={classes.toolDisplay}>{
        assetTools.map((tool, index) => {
          const onlineTag = tool.tagReport;
          const loading = fetchingTool.busy && fetchingTool.tool === tool.id;
          return (
            loading ? (
              <div className={classes.toolItem} key={`loading-${tool.id}`}>
                <LoadingText />
              </div>
            ) : (
              <div key={`${tool.name}${index}`} className={classes.toolItem} onClick={function(e) {
                selectTool(e, tool);
              }}>
                <div className={classes.iconContainer}>
                  {getAssetIconType(asset, tool) === AssetType?.ScDevice
                    ? <ScDeviceIcon color={dlsColor('grey3')}
                      size={IconSize.small} />
                    : <AssetTypeIcon assetType={AssetType[tool.assetType || 'Tool']}
                      color={dlsColor('grey3')}
                      size={IconSize.small} />}
                </div>
                <Typography className={classes.toolText}>
                  {tool.name}
                </Typography>
                {!asset.getAssetValue('tagReports') && !onlineTag && (
                  <NotificationUrgentIcon color={dlsColor('caRed')} size='14' style={{padding: '3px'}} />
                )}
              </div>
            )
          );
        })
      }
      </div>
    ) : <NoTools classes={classes} error={toolsError} retry={retry} />
  );

  const wrapperStyle = isMatchingAsset?.open
    ? classes.containedAssetWrapperOpen
    : classes.containedAssetWrapper;

  return (
    <div className={wrapperStyle}>
      {isMatchingAsset && (
        <div className={classes.container}>
          {isToolsLoading?.toolsLoading
            ? <div className={classes.spinnerWrapper}><IonSpinner /></div>
            : (!assetTools?.length
              ? <NoTools classes={classes} error={toolsError} retry={retry} />
              : <ToolListDisplay />)}
        </div>
      )}
    </div>
  );
};

export default ContainedAsset;
